import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef } from "react";

// paint-order hides the inner half of the stroke, so joined Persian letters show one clean outline.
// Story phrases set in the app's fonts: a type specimen, decorative, so hidden from screen readers.
const PHRASES: [string, string][] = [
  ["تولدت مبارک", "font-display font-black text-ink"],
  ["یه فنجون قهوه", "font-alt text-ink-2 [font-variation-settings:'wght'_200,'wdth'_100]"],
  ["جمعه", "font-display font-black text-bg-2 [-webkit-text-stroke:3px_var(--c-accent-text)] [paint-order:stroke_fill]"],
  ["دلم برات تنگ شده", "font-body font-extralight text-ink"],
  ["صبح بخیر", "font-alt text-accent-text [font-variation-settings:'wght'_850,'wdth'_70]"],
  ["شب بخیر", "font-display font-medium text-ink-3"],
];

// The page's only marquee: scroll position drives the band sideways, so it moves only while you move.
export default function TypeBand() {
  const band = useRef<HTMLElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const dist = useRef(0);
  const { scrollYProgress } = useScroll({ target: band, offset: ["start end", "end start"] });
  const x = useTransform(scrollYProgress, (v) => (v - 1) * dist.current);

  useEffect(() => {
    const b = band.current, tr = track.current;
    if (!b || !tr) return;
    const ro = new ResizeObserver(() => (dist.current = Math.max(0, tr.scrollWidth - b.clientWidth)));
    ro.observe(b);
    ro.observe(tr);
    return () => ro.disconnect();
  }, []);

  return (
    <section ref={band} aria-hidden className="overflow-hidden border-y border-line bg-bg-2 py-[clamp(56px,8vw,104px)]" dir="ltr">
      <motion.div
        ref={track}
        style={{ x }}
        // reduced motion: a still, centered specimen (CSS, so server and client markup match)
        className="flex w-max items-baseline gap-[clamp(36px,5vw,80px)] whitespace-nowrap px-[var(--gutter)] text-[clamp(3.25rem,1.6rem+6.4vw,8.5rem)] leading-[1.35] motion-reduce:w-auto motion-reduce:flex-wrap motion-reduce:justify-center motion-reduce:gap-y-2 motion-reduce:whitespace-normal motion-reduce:text-[clamp(2.5rem,1.6rem+4vw,5rem)] motion-reduce:![transform:none]"
      >
        {PHRASES.map(([text, cls]) => (
          <span key={text} dir="rtl" className={cls}>
            {text}
          </span>
        ))}
      </motion.div>
    </section>
  );
}
